/* eslint-disable no-case-declarations */
/* eslint-disable no-underscore-dangle, max-len */
import { Logger } from 'vue-infra';
import { each, get, includes, map, isEqual, sortBy, isNull } from 'lodash';
import { BotsAlertsService } from '@core/services/alerts/bots/BotsAlertsService';
import BotsListService from '@core/services/bot-list/BotsListService';
import {
  RETRY_INTERVAL,
  UPDATE_SUBSCRIPTION_MODE,
  WIDGET_LOGICAL_NAMES,
  AGENT_STATUSES,
  DATA_LOADING_STATES,
  METRIC_NAMES,
  WIDGET_MODES,
} from '@config/Consts';
import { Subscription } from '@core/common/Subscription';
import { isMaxNumRetriesReached, getInterceptor, getSubscriptionsByMode, isEventRelevant } from '@core/utils/utils';
import { ROOT_GETTER_PATHS } from '@core/store';
import { RESOURCES_MODEL_MAP, RESOURCES_METRICS_MAP, BOT_LIST_RESOURCES } from './Consts';

const logger = Logger.getLogger('botsList');

const SUBSCRIPTION_BY_MODE = {
  [UPDATE_SUBSCRIPTION_MODE.PRIMARY]: [
    BOT_LIST_RESOURCES.AGENT_VIEW,
    BOT_LIST_RESOURCES.METRICS,
  ],
  [UPDATE_SUBSCRIPTION_MODE.SECONDARY]: [
    BOT_LIST_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS,
  ],
};

const changeWidgetMode = ({ commit }, mode) => {
  commit('setWidgetMode', mode);
};

const _getSubscriptionsByMode = async (mode, getters) => {
  const subscriptions = await BotsListService.getBotsListSubscriptions({
    interceptor: getInterceptor.bind(this, getters),
  });
  return getSubscriptionsByMode(subscriptions, SUBSCRIPTION_BY_MODE, mode);
};

const _isGraphSubscription = subscription => subscription.name === BOT_LIST_RESOURCES.HISTORICAL;

const _createSubscriptionPayload = (getters, isGraphView = false) => {
  logger.debug('Bot List::actions::_createSubscriptionPayload', { isGraphView });
  const graphFocusedBotId = getters.getGraphFocusBotId;
  let filteredBots = getters.getBotsFilter;
  if (isGraphView && !isNull(graphFocusedBotId)) {
    filteredBots = [graphFocusedBotId];
  }
  let payload = {
    timeframe: getters.getTimeFrameFilter,
    currentPageBotIds: isNull(graphFocusedBotId) ? getters.getCurrentPageBotIds.toString() : '',
    filter: {
      skills: isNull(graphFocusedBotId) ? getters.getSkillsFilter : [],
      bots: filteredBots,
      groupIds: isNull(graphFocusedBotId) ? getters.getFilteredGroupsIds : [],
    },
  };
  if (!isGraphView) {
    payload = {
      ...payload,
      paging: getters.getPaginationConfigForAPI,
      sort: getters.getSort,
    };
    payload.filter.status = getters.getStatusFilter;
  }
  return payload;
};

const _updateGraphSubscription = async ({ getters }) => {
  const subscription = await BotsListService.getBotsGraphSubscription();
  const subscriptionPayload = _createSubscriptionPayload(getters, true);
  try {
    const updateData = { settings: subscriptionPayload, name: subscription.name };
    await subscription.subscription.update(updateData);
  } catch (ex) {
    logger.error('cannot update Bot graph subscription', 'update', { ex, subscription });
  }
};

const updateSubscription = async ({ commit, getters, rootGetters }, data) => {
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.BOTS)) {
    logger.debug('Bot List::no permission to update the bot list subscription');
    return;
  }
  const DEFAULT_UPDATE_SUBSCRIPTION_DATA = {
    updateAgentViewApi: false,
    resetPageNumber: true,
    subscriptionsMode: UPDATE_SUBSCRIPTION_MODE.PRIMARY,
  };
  const dataParams = Object.assign(DEFAULT_UPDATE_SUBSCRIPTION_DATA, data);
  const graphIsActivated = getters.graphIsActivated;

  if (dataParams.subscriptionsMode === UPDATE_SUBSCRIPTION_MODE.PRIMARY) {
    // we need to update PagingPageNumber just when updating search agents
    // (which is included in UPDATE_SUBSCRIPTION_MODE.PRIMARY)
    // as in this case we reset the state
    if (dataParams?.resetPageNumber) {
      commit('setPagination', { key: 'currentPage', value: 1 });
    }
    if (graphIsActivated) {
      // we update the graph subscription only when the primary subscription is updated
      await _updateGraphSubscription({ getters });
    }
  }

  const subscriptions =
    await _getSubscriptionsByMode(dataParams.subscriptionsMode, getters);
  if (subscriptions) {
    each(subscriptions, async (subscription) => {
      const isGraphSubscription = _isGraphSubscription(subscription);
      // if graph is not activated and subscription is graph do not update the subscription. done previously
      const proceedWithUpdateSubscription = !isGraphSubscription || (isGraphSubscription && graphIsActivated);
      // we need to update AgentStatus just in specific cases in this widget
      if (proceedWithUpdateSubscription && (subscription.name !== BOT_LIST_RESOURCES.METRICS ||
        dataParams.updateAgentViewApi)) {
        const subscriptionPayload = _createSubscriptionPayload(getters);
        try {
          const updateData = { settings: subscriptionPayload, name: subscription.name };
          await subscription.subscription.update(updateData);
        } catch (ex) {
          logger.error('cannot update Bot List subscription', 'update', { ex, subscription });
        }
      }
    });
  }
};

const _getBotsMetricsData = (responseData, resourceName) => {
  // eslint-disable-next-line prefer-const
  let botsMetrics = [];

  each(responseData, (botData) => {
    const botItem = { id: botData.id };
    const metrics = [];
    if (botData && botData.metrics instanceof RESOURCES_MODEL_MAP[resourceName]) {
      each(RESOURCES_METRICS_MAP[resourceName], (metricName) => {
        metrics.push({ metric: metricName, score: botData.metrics[metricName] });
      });
      botItem.metrics = metrics;
    }
    botsMetrics.push(botItem);
  });

  return botsMetrics;
};

const _handleAgentViewResource = ({ commit, getters, rootGetters }, resourceName, responseData) => {
  logger.debug('Bot List::actions::_handleAgentViewResource');
  let dataStatus;
  let botsData;
  // eslint-disable-next-line prefer-const
  let botsMetrics = [];

  if (responseData) {
    dataStatus = DATA_LOADING_STATES.SUCCESS;
    botsData = responseData.data;
    botsMetrics = _getBotsMetricsData(botsData, resourceName);

    if (responseData.botsCount) {
      commit('setPagination', { key: 'totalPages', value: responseData.botsCount });
    }

    const responseBotIds = map(botsData, 'id');
    if (!isEqual(sortBy(responseBotIds), sortBy(getters.getCurrentPageBotIds))) {
      commit('setCurrentPageBotIds', responseBotIds);
      updateSubscription({ commit, getters, rootGetters }, { subscriptionsMode: UPDATE_SUBSCRIPTION_MODE.SECONDARY });
    }
  } else if (getters.dataHasBeenFetched) {
    dataStatus = DATA_LOADING_STATES.OBSOLETE;
  } else {
    dataStatus = DATA_LOADING_STATES.ERROR;
  }

  commit('updateBotsMetrics', { botsMetrics, resourceName, dataStatus });
};

const _handleHistoricalResource = ({ commit }, responseData) => {
  const dataStatus = DATA_LOADING_STATES.SUCCESS;
  const graphMetrics = responseData.historicalMetricsIntervals || [];
  commit('updateGraphMetrics', { graphMetrics, dataStatus });
};

const _handleMetricsResource = ({ commit, getters }, resourceName, responseData) => {
  logger.debug('Bot List::actions::_handleMetricsResource');
  let dataStatus = DATA_LOADING_STATES.NONE;

  if (responseData) {
    dataStatus = DATA_LOADING_STATES.SUCCESS;
  } else if (getters.dataHasBeenFetched) {
    dataStatus = DATA_LOADING_STATES.OBSOLETE;
  }

  const statusSummary = {
    [AGENT_STATUSES.AWAY]: get(responseData, METRIC_NAMES.AGENT_VIEW_METRICS_AWAY_AGENTS, 0),
    [AGENT_STATUSES.BACK_SOON]: get(responseData, METRIC_NAMES.AGENT_VIEW_METRICS_BACK_SOON_AGENTS, 0),
    [AGENT_STATUSES.ONLINE]: get(responseData, METRIC_NAMES.AGENT_VIEW_METRICS_ONLINE_AGENTS, 0),
  };

  commit('setBotStatusSummary', { statusSummary, dataStatus });
};

const _handleCsatResource = ({ commit, getters }, resourceName, responseData) => {
  logger.debug('Bot List::actions::_handleCsatResource');
  let dataStatus = DATA_LOADING_STATES.NONE;
  let botsMetrics;

  if (responseData) {
    dataStatus = DATA_LOADING_STATES.SUCCESS;
    botsMetrics = _getBotsMetricsData(responseData, resourceName);
  } else if (getters.dataHasBeenFetched) {
    dataStatus = DATA_LOADING_STATES.OBSOLETE;
  }

  commit('updateBotsMetrics', { botsMetrics, resourceName, dataStatus });
};

const _handleSubscriptionSuccess = (context, resourceName, response) => {
  logger.debug('Bot List::actions::_handleSubscriptionSuccess');
  const { commit, getters, rootGetters, dispatch } = context;
  const responseData = response.data;

  switch (resourceName) {
    case BOT_LIST_RESOURCES.AGENT_VIEW:
      _handleAgentViewResource({ commit, getters, rootGetters }, resourceName, responseData);
      break;
    case BOT_LIST_RESOURCES.METRICS:
      _handleMetricsResource({ commit, getters }, resourceName, responseData);
      break;
    case BOT_LIST_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS:
      _handleCsatResource({ commit, getters }, resourceName, responseData);
      break;
    case BOT_LIST_RESOURCES.HISTORICAL:
      const data = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_USE_BOTS_GRAPH_MOCK] ?
        rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_BOTS_GRAPH_MOCK] : responseData;
      _handleHistoricalResource({ commit }, data);
      break;
    default:
      break;
  }
  dispatch('evaluateMetricsForAlerts');
};

const _onSubscriptionUpdateNotification =
  (context, resourceName, response) => {
    logger.debug('Bot List::actions::_onSubscriptionUpdateNotification', { resourceName, response });
    _handleSubscriptionSuccess(context, resourceName, response);
  };

const _onSubscriptionError = ({ commit, getters, rootGetters }, resourceName, err) => {
  if (!err.cancelled) {
    logger.error('Bot List::actions::_onSubscriptionError', JSON.stringify({ resourceName, err }));
    commit('setDataStatus', { resourceName, status: DATA_LOADING_STATES.ERROR });
  }

  if (isMaxNumRetriesReached(err)) {
    const subscriptionsMode =
      includes(SUBSCRIPTION_BY_MODE[UPDATE_SUBSCRIPTION_MODE.PRIMARY], resourceName) ?
        UPDATE_SUBSCRIPTION_MODE.PRIMARY : UPDATE_SUBSCRIPTION_MODE.SECONDARY;
    const data = { subscriptionsMode };
    if (subscriptionsMode === UPDATE_SUBSCRIPTION_MODE.PRIMARY) {
      data.updateAgentViewApi = true;
    }
    // eslint-disable-next-line no-use-before-define
    setTimeout(() => updateSubscription({ commit, getters, rootGetters }, data), RETRY_INTERVAL);
  }
};

const _handleSubscriptions = async (context, subscriptions) => {
  const { commit, getters, rootGetters } = context;
  if (subscriptions && subscriptions.subscriptions) {
    const graphIsActivated = getters.graphIsActivated;
    each(subscriptions.subscriptions, (subscription) => {
      const isGraphSubscription = _isGraphSubscription(subscription);
      // if graph is not activated and subscription is graph do not start the subscription
      const proceedWithStartSubscription = !isGraphSubscription || (isGraphSubscription && graphIsActivated);
      if (proceedWithStartSubscription) {
        commit('setDataStatus', { resourceName: subscription.name, status: DATA_LOADING_STATES.LOADING });
      }
      subscription.subscription.clearAllEventListeners();
      subscription.subscription.on(Subscription.EVENTS.onSubscriptionUpdateNotification,
        _onSubscriptionUpdateNotification.bind(this,
          context, subscription.name),
        'botSubscriptionUpdateNotificationSuccess');
      subscription.subscription.on(Subscription.EVENTS.onSubscriptionError,
        _onSubscriptionError.bind(this, { commit, getters, rootGetters }, subscription.name),
        'botSubscriptionUpdateNotificationError');
      if (proceedWithStartSubscription) {
        const subscriptionPayload = _createSubscriptionPayload(getters);
        try {
          logger.debug('Bot List::actions::before executing start subscription');
          subscription.subscription.start({ settings: subscriptionPayload,
            name: subscription.name });
        } catch (ex) {
          logger.error('cannot start Bot List subscription', 'start', { ex, subscription });
        }
      }
    });
  }
};

const _handleGraphSubscription = async (context, subscription) => {
  const { commit, getters } = context;
  if (subscription) {
    const resourceName = BOT_LIST_RESOURCES.HISTORICAL;
    commit('setDataStatus', { resourceName, status: DATA_LOADING_STATES.LOADING });
    const subscriptionPayload = _createSubscriptionPayload(getters, true);
    try {
      logger.debug('Bot List::actions::before executing graph start subscription');
      subscription.subscription.start({ settings: subscriptionPayload,
        name: subscription.name });
      commit('setGraphActivated', true);
    } catch (ex) {
      logger.error('cannot start Bot List graph subscription', 'start', { ex, subscription });
    }
  }
};

const startGraphSubscription = async (context) => {
  const { rootGetters } = context;
  logger.debug('Bot List::actions::startGraphSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.BOTS)) {
    logger.debug('Bot List::no permission to start the bot graph widget subscription');
    return;
  }
  const graphSubscription = await BotsListService.getBotsGraphSubscription();
  await _handleGraphSubscription(context, graphSubscription);
};

const startSubscription = async (context) => {
  const { getters, rootGetters } = context;
  logger.debug('Bot List::actions::startSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.BOTS)) {
    logger.debug('Bot List::no permission to start the bot list widget subscription');
    return;
  }
  const subscriptions = await BotsListService.getBotsListSubscriptions({
    interceptor: getInterceptor.bind(this, getters),
  });
  await _handleSubscriptions(context, subscriptions);
  const widgetMode = get(getters.getBotsGraphUserSettings, 'widgetMode');
  if (getters.getShowGraphFromManifest &&
    widgetMode && includes([WIDGET_MODES.TABLE_AND_GRAPH, WIDGET_MODES.CARDS_AND_GRAPH],
    widgetMode)) {
    await startGraphSubscription(context);
  }
};

const stopSubscription = async (context) => {
  const { commit, getters, rootGetters } = context;
  logger.debug('Bot List::actions::stopSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.BOTS)) {
    logger.debug('Bot List::no permission to stop the bot list widget subscription');
    return;
  }
  const subscriptions = await BotsListService.getBotsListSubscriptions({
    interceptor: getInterceptor.bind(this, getters),
  });
  if (subscriptions && subscriptions.subscriptions) {
    each(subscriptions.subscriptions, (subscription) => {
      subscription.subscription.clearAllEventListeners();
      try {
        logger.debug('Bot List::actions::before executing stop subscription');
        subscription.subscription.stop({ name: subscription.name });
        commit('setDataStatus', { resourceName: subscription.name, status: DATA_LOADING_STATES.NONE });
      } catch (ex) {
        logger.error('cannot stop Bot List subscription', 'stop', { ex, subscription });
      }
    });
  }
};

const changeStatusFilter = ({ commit, getters, rootGetters }, status) => {
  commit('setStatusFilter', status);

  return updateSubscription({ commit, getters, rootGetters });
};

const changeBotsFilter = ({ commit, getters, rootGetters }, botsFilterList) => {
  commit('setBotsFilter', botsFilterList);
  const data = { updateAgentViewApi: true };
  return updateSubscription({ commit, getters, rootGetters }, data);
};

const changeSort = ({ commit, getters, rootGetters }, sort) => {
  commit('setSort', sort);
  return updateSubscription({ commit, getters, rootGetters });
};

const setGraphFocusBotId = async ({ commit, getters }, graphFocusBotId) => {
  commit('setGraphFocusBotId', graphFocusBotId);
  if (getters.graphIsActivated) {
    await _updateGraphSubscription({ getters });
  }
};

const applyPresetValues = ({ commit }, preset) => {
  commit('setPresetValues', preset);
};

const handleStartSubscription = (context, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.BOTS, widgets)) {
    startSubscription(context);
  }
};
const handleStopSubscription = (context, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.BOTS, widgets)) {
    stopSubscription(context);
  }
};
const evaluateMetricsForAlerts = async ({ getters, state }) => {
  const alertPermissions = getters.getAlertPermissions;
  const alertData = getters.getAlertData;
  if (alertPermissions.canViewAlert) {
    state.botsMetrics = await BotsAlertsService.evaluateMetrics(state.botsMetrics, alertData);
  }
};
const handleUpdateSubscription = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.BOTS, widgets)) {
    const primarySubscriptionsData =
    {
      updateAgentStatusApi: true,
    };
    const secondarySubscriptionsData =
    {
      subscriptionsMode: UPDATE_SUBSCRIPTION_MODE.SECONDARY,
    };
    updateSubscription({ commit, getters, rootGetters }, primarySubscriptionsData);
    updateSubscription({ commit, getters, rootGetters }, secondarySubscriptionsData);
  }
};
const handleShowWidgets = (context, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.BOTS, widgets)) {
    startSubscription(context);
  } else {
    stopSubscription(context);
  }
};

export default {
  SUBSCRIPTION_BY_MODE,
  RESOURCES_METRICS_MAP,
  UPDATE_SUBSCRIPTION_MODE,
  changeWidgetMode,
  changeStatusFilter,
  changeBotsFilter,
  changeSort,
  startGraphSubscription,
  setGraphFocusBotId,
  applyPresetValues,
  handleStartSubscription,
  handleStopSubscription,
  handleUpdateSubscription,
  handleShowWidgets,
  updateSubscription, // needed for pagination
  evaluateMetricsForAlerts,
};
